"use client";
import React, { useState, useCallback } from "react";
import { Menu, X } from "lucide-react";
import GradualBlurMemo from "@/components/blur/GradualBlur";
import Link from "next/link";
import Image from "next/image";
import dynamic from "next/dynamic";
import { useLenisInstance } from "./LenisProvider";
import { useRouter, usePathname } from "next/navigation";
import { openCalModal } from "./PageWithCalModal";
import {
  trackCTAClicked,
  trackMeetingModalOpened,
  trackNavLinkClicked,
} from "@/lib/analytics";

const MobileMenu = dynamic(() => import("./MobileMenu"), { ssr: false });

interface NavLink {
  id: number;
  label: string;
  url: string;
  openInNewTab: boolean;
}

interface NavbarProps {
  firstScrollEnabled?: boolean;
  navLinks?: NavLink[];
  ctaButtonLabel?: string;
  ctaButtonUrl?: string;
}

const defaultNavLinks: NavLink[] = [
  {
    id: 17,
    label: "For Hosts",
    url: "https://dinner.country.club/",
    openInNewTab: true,
  },
  {
    id: 18,
    label: "For Sponsors",
    url: "https://sponsor.country.club/",
    openInNewTab: true,
  },
  {
    id: 19,
    label: "For Conferences",
    url: "https://conference.country.club/",
    openInNewTab: true,
  },
];

export default function Navbar({
  firstScrollEnabled = true,
  navLinks = defaultNavLinks,
  ctaButtonLabel = "Request Demo",
  ctaButtonUrl = "request_demo",
}: NavbarProps) {
  const [open, setOpen] = useState(false);
  const toggle = useCallback(() => setOpen((o) => !o), []);
  const close = useCallback(() => setOpen(false), []);
  const lenis = useLenisInstance();
  const router = useRouter();
  const pathname = usePathname();

  const handleRequestDemo = useCallback(() => {
    trackCTAClicked({
      cta_location: "header",
      cta_type: "demo",
      cta_text: ctaButtonLabel,
    });
    trackMeetingModalOpened("navbar");
    openCalModal("momentumworld/30min");
    // window.location.href = "/apply-member";
  }, [ctaButtonLabel]);

  const handleNavClick = useCallback(
    (
      e: React.MouseEvent<HTMLElement>,
      url: string,
      openInNewTab: boolean,
      label?: string,
    ) => {
      trackNavLinkClicked(
        label || url,
        url,
        openInNewTab || !url.startsWith("#"),
        "desktop",
      );
      if (url.startsWith("#")) {
        e.preventDefault();
        const id = url.slice(1);

        if (pathname === "/") {
          const el = document.getElementById(id);
          if (el && lenis) {
            lenis.scrollTo(el, { offset: -80 });
          } else if (el) {
            const yOffset = -80;
            const y = el.offsetTop + yOffset;
            window.scrollTo({ top: y, behavior: "smooth" });
          }
        } else {
          router.push(`/?scrollTo=${id}`);
        }
      } else if (openInNewTab) {
        e.preventDefault();
        window.open(url, "_blank", "noopener,noreferrer");
      }
    },
    [lenis, pathname, router],
  );

  return (
    <header className="fixed top-0 left-0 right-0 z-50 px-6 md:px-12 py-6">
      <div className="max-w-7xl mx-auto relative z-50 flex items-center justify-between">
        <Link
          href="https://country.club"
          className="hover:opacity-80 transition-opacity"
          aria-label="Go to Country Club"
        >
          <img
            src="/branding/logo.webp"
            alt="Country Club Lunch"
            className="h-8 sm:h-10"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-6">
          {navLinks.map((item) => (
            <Link
              key={item.id}
              href={item.url}
              onClick={(e) =>
                handleNavClick(e, item.url, item.openInNewTab, item.label)
              }
              {...(item.openInNewTab
                ? { target: "_blank", rel: "noopener noreferrer" }
                : {})}
              className="px-5 py-2.5 text-sm text-white/80 hover:text-white transition-colors"
            >
              {item.label}
            </Link>
          ))}

          <button
            onClick={handleRequestDemo}
            className="px-5 py-2.5 text-sm border border-white rounded-lg text-white hover:bg-white/10 transition-all"
          >
            {ctaButtonLabel}
          </button>
        </nav>

        <button type="button" className="md:hidden text-white" onClick={toggle}>
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <GradualBlurMemo
        position="top"
        height="5rem"
        strength={1.5}
        divCount={4}
        curve="ease-out"
        exponential={false}
        opacity={0.9}
        zIndex={1}
      />

      <MobileMenu
        open={open}
        onClose={close}
        navLinks={navLinks}
        ctaButtonLabel={ctaButtonLabel}
        ctaButtonUrl={ctaButtonUrl}
        onRequestDemo={handleRequestDemo}
      />
    </header>
  );
}
